import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { Shield, Users, Brain, Lock } from 'lucide-react';

export function Safety() {
  const { theme } = useTheme();

  const principles = [
    {
      icon: Shield,
      title: "Safety First",
      description: "Our AI systems are developed with robust safety measures and continuous testing."
    },
    {
      icon: Users,
      title: "Human-Centered",
      description: "We prioritize human values and ensure AI systems align with societal benefits."
    },
    {
      icon: Brain,
      title: "Transparency",
      description: "Our research and development processes are open and accountable."
    },
    {
      icon: Lock,
      title: "Privacy & Security",
      description: "Strong safeguards protect user data and system integrity."
    }
  ];

  const frameworks = [
    {
      title: "Ethics Review Board",
      description: "Independent oversight ensuring ethical AI development",
      items: [
        "Project assessment",
        "Risk evaluation",
        "Ethical guidelines",
        "Regular audits"
      ]
    },
    {
      title: "Safety Protocols",
      description: "Comprehensive safety measures throughout development",
      items: [
        "Testing frameworks",
        "Monitoring systems",
        "Emergency controls",
        "Impact assessment"
      ]
    }
  ];

  return (
    <>
      <Background3D />
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="max-w-[1440px] mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h1 className="text-6xl font-normal mb-8 leading-tight">
                  Building <span className="text-gradient">safe</span> and <span className="text-gradient">ethical</span> AI systems
                </h1>
                <p className={`text-xl leading-relaxed mb-8 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Our commitment to safety and ethics is at the core of everything we do. 
                  We're dedicated to developing AI that benefits humanity while minimizing potential risks.
                </p>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-radial from-primary-500/20 to-transparent opacity-70"></div>
                <img
                  src="https://images.unsplash.com/photo-1633167606207-d840b5070fc2?auto=format&fit=crop&q=80"
                  alt="AI Safety"
                  className="w-full h-[500px] object-cover rounded-3xl shadow-2xl hover-card"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Principles */}
        <section className={`py-32 ${theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/50'}`}>
          <div className="max-w-[1440px] mx-auto px-6">
            <h2 className="text-4xl font-normal mb-16">Our Principles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {principles.map((principle, index) => (
                <div
                  key={index}
                  className={`p-8 rounded-2xl hover-card ${
                    theme === 'dark' ? 'bg-dark-300' : 'bg-white'
                  }`}
                >
                  <div className="mb-6">
                    <principle.icon className="w-12 h-12 text-primary-500" />
                  </div>
                  <h3 className="text-2xl font-normal mb-4">{principle.title}</h3>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    {principle.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Frameworks */}
        <section className="py-32">
          <div className="max-w-[1440px] mx-auto px-6">
            <h2 className="text-4xl font-normal mb-16">Safety Frameworks</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {frameworks.map((framework, index) => (
                <div
                  key={index}
                  className={`p-8 rounded-2xl hover-card ${
                    theme === 'dark' ? 'bg-dark-300' : 'bg-white'
                  }`}
                >
                  <h3 className="text-2xl font-normal mb-4">{framework.title}</h3>
                  <p className={`mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {framework.description}
                  </p>
                  <ul className="space-y-3">
                    {framework.items.map((item, itemIndex) => (
                      <li
                        key={itemIndex}
                        className={`flex items-center ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        <div className="w-2 h-2 rounded-full bg-primary-500 mr-3"></div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}